/** CX-REFACTOR: Thise kind of checks and data should be moved into question-modules
 */

import { MentiError } from '@mentimeter/error-utils/error';
import type { ModuleId, Question } from '@mentimeter/http-clients';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import {
  ALL_QUIZ_QUESTION_TYPES,
  ALL_QUIZ_SLIDE_TYPES,
  INTERACTIVE_SLIDES_TYPES,
  QUIZ_SLIDES_TYPES,
  RESTRICTED_INTEGRATION_SLIDES_TYPES,
} from '../constants/slideTypes';
import { getFollowingLeaderboardId, getSlideById } from './slides';

export function isRestrictedInteractiveQuestion(
  question: Question | undefined,
) {
  return INTERACTIVE_SLIDES_TYPES.some(
    (qt) => question?.type === qt.type && !qt.unrestricted,
  );
}

export function isRestrictedQuizQuestion(question: Question | undefined) {
  return ALL_QUIZ_QUESTION_TYPES.some(
    (qt) => question?.type === qt.type && !qt.unrestricted,
  );
}

export function isRestrictedIntegrationQuestion(
  moduleId: ModuleId | undefined,
) {
  return RESTRICTED_INTEGRATION_SLIDES_TYPES.some(
    (qt) => qt.moduleId === moduleId,
  );
}

export const isLeaderboardSlide = (
  questionType: Question['type'] | undefined,
) => questionType === 'quiz_leaderboard';

export const isTypeQuizQuestion = (questionType: Question['type']) =>
  ALL_QUIZ_QUESTION_TYPES.map((q) => q.type).includes(questionType);

/**
 * @deprecated Use isQuizModule instead
 */
export const isTypeQuizSlide = (questionType: Question['type'] | undefined) =>
  Boolean(questionType && QUIZ_SLIDES_TYPES.includes(questionType));

export const getIdToInsertAfter = (
  currentQuestion: Question | undefined,
  questions: Question[],
  isMobile?: boolean,
) => {
  if (!currentQuestion) return undefined;

  if (isMobile) return questions[questions.length - 1]!.id;

  const directlyFollowingLeaderboardId = getFollowingLeaderboardId(
    questions,
    currentQuestion.id,
  );
  if (
    currentQuestion?.type &&
    isTypeQuizQuestion(currentQuestion?.type) &&
    directlyFollowingLeaderboardId
  ) {
    return directlyFollowingLeaderboardId;
  }

  return currentQuestion.id;
};

export const calculateDeleteQuestionOperation = (
  questions: Array<Question>,
  activeQuestionId: string,
  questionIdsToDelete: Array<string>,
): { nextActiveQuestionId: string; questionIdsToDelete: Array<string> } => {
  const additionalIdsToDelete = flatten(
    questionIdsToDelete.map((id) => {
      const [question] = getSlideById(id, questions);
      if (!question) {
        throw new MentiError(
          'calculateDeleteQuestionOperation: Could not find active question',
          {
            feature: 'editing-in-editor',
          },
        );
      }
      return getDependantQuestions(questions, question);
    }),
  );

  const allQuestionIdsToDelete = uniq<string>([
    ...questionIdsToDelete,
    ...additionalIdsToDelete,
  ]);

  const nextActiveQuestionId = calculateNextActiveQuestionId(
    questions,
    activeQuestionId,
    allQuestionIdsToDelete,
  );

  return {
    nextActiveQuestionId,
    questionIdsToDelete: allQuestionIdsToDelete,
  };
};

const getDependantQuestions = (
  questions: Array<Question>,
  question: Question,
) => {
  if (!question || !isTypeQuizQuestion(question.type)) {
    return [];
  }
  const numOfQuizQuestions = questions.filter((q) =>
    isTypeQuizQuestion(q.type),
  ).length;
  if (numOfQuizQuestions === 1) {
    return questions.filter((q) => isLeaderboardSlide(q.type)).map((q) => q.id);
  }
  const directlyFollowingLeaderboardId = getFollowingLeaderboardId(
    questions,
    question.id,
  );
  if (directlyFollowingLeaderboardId) {
    return [directlyFollowingLeaderboardId];
  }
  return [];
};

const calculateNextActiveQuestionId = (
  questions: Array<Question>,
  activeQuestionId: string,
  questionIdsToDelete: string[],
) => {
  const isActiveQuestionBeingDeleted =
    questionIdsToDelete.includes(activeQuestionId);
  if (isActiveQuestionBeingDeleted) {
    const questionsLeft = questions.filter(
      (q) => !questionIdsToDelete.includes(q.id),
    );

    const currentIndex = questions.findIndex((q) => q.id === activeQuestionId);
    const newIndex = currentIndex - 1 > 0 ? currentIndex - 1 : 0;
    return (
      questionsLeft[newIndex]?.id ??
      questionsLeft[questionsLeft.length - 1]?.id ??
      activeQuestionId
    );
  }
  return activeQuestionId;
};

export const getQuizQuestions = (questions: Array<Question>) =>
  questions.filter((q) =>
    ALL_QUIZ_QUESTION_TYPES.some((qt) => q.type === qt.type),
  );

export const getQuizQuestionsWithLeaderboard = (questions: Array<Question>) =>
  questions.filter((q) =>
    ALL_QUIZ_SLIDE_TYPES.some((qt) => q.type === qt.type),
  );

export const findLastQuizSlideIndex = (questions: Array<Question>) => {
  const quizSlides = getQuizQuestionsWithLeaderboard(questions);
  const lastQuizSlideId = quizSlides[quizSlides.length - 1]?.id ?? '';
  return questions.findIndex((question) => question.id === lastQuizSlideId);
};

export const findDirectlyFollowingLeaderboardById = (
  questions: Array<Question>,
  relativeQuestionId: string | undefined,
): Question | null => {
  if (!relativeQuestionId) {
    return null;
  }

  const index = questions.findIndex((q) => q.id === relativeQuestionId);
  try {
    const nextQuestion = questions[index + 1];
    if (nextQuestion) {
      if (isLeaderboardSlide(nextQuestion.type)) {
        return nextQuestion;
      }
    }

    return null;
  } catch {
    return null;
  }
};
